import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TrackJS } from 'trackjs';
import get from 'lodash/get';
import Timer from '@wetransfer/concorde-timer';

import WallpaperConfig from './config';
import HTML5Api from './html5-api';
import Media from './media';
import OpenXProvider from './provider/openx';
import Position from './position';
import Predefined from './provider/predefined';
import Preloader from './preloader';
import Visibility from './visibility';

import Dropzone from '@component/dropzone';

import Actions from '@app/action';
import FeatureFlagsHelper from '@app/helper/featureflags';
import Analytics from '@module/analytics';
import Metric from '@module/metric';
import Vast from '@module/openx/vast';
import Router from '@module/router';

// We are using the Wallpaper component as a singleton
let consecutiveErrors = 0;
let currentWallpaper = null;
let isPaused = false;

const TRACKER_ELEMENTS = {
  static: [],
  dynamic: [],
};
const MAX_TITLE_LENGTH = 150;

export class Wallpaper extends React.Component {
  /**
   * Binds all private methods
   * @param  {Object} props Component props
   */
  constructor(props) {
    super(props);

    this.currentMedia = false;
    this.visibleMedia = [];
    this.provider = false;
    this.timer = null;
    this.html5Api = new HTML5Api(this);

    this.determineWallpaperProvider = this.determineWallpaperProvider.bind(
      this
    );
    this.initFrame = this.initFrame.bind(this);
    this.repositionMedia = this.repositionMedia.bind(this);
  }

  // When the component is mounted we want to listen for window
  // resize events, to adjust the media dimensions and/or position.
  UNSAFE_componentWillMount() {
    window.addEventListener('resize', this.repositionMedia, false);
  }

  // Keep track of the component instance.
  componentDidMount() {
    currentWallpaper = this;
  }

  // When component is updated
  componentDidUpdate(prevProps) {
    const {
      advert_id,
      route,
      user: { loggedIn },
    } = this.props;

    if (this.currentMedia) {
      this.html5Api.setPanelState(route.type !== 'transfer');
    }

    // Listen for updates on tandc & advert_id here, because props get updated after route has changed.
    const changedAdvertId = prevProps.advert_id !== advert_id;
    const changedLogin = prevProps.user.loggedIn !== loggedIn;
    if (changedAdvertId || changedLogin) {
      this.determineWallpaperProvider();
    }
    // We pause the rotation if the splashpage is active or when the panel is open.
    if (this.isPanelOpen() === true || route.path === '/welcome') {
      this.pauseRotation();
    } else {
      this.resumeRotation();
    }
  }

  // When the component is unmounted we want to remove our custom
  // event listeners and clear the rotation timer.
  componentWillUnmount() {
    currentWallpaper = null;
    window.removeEventListener('resize', this.repositionMedia, false);

    if (this.timer) {
      this.timer.stop();
      this.timer = null;
    }
  }

  isPanelOpen() {
    return this.props.route.type === 'page' || this.props.route.type === 'plus';
  }

  getContentDocument() {
    try {
      return this.frameNode.contentDocument
        ? this.frameNode.contentDocument
        : false;
    } catch (e) {
      if (this.currentMedia) {
        TrackJS.addMetadata('file', this.currentMedia.file);
      }
      TrackJS.track('FrameUnavailable');
    }

    return false;
  }

  // Start the iFrame
  initFrame() {
    // Are we showing OpenX or Predefined wallpapers?
    this.determineWallpaperProvider();

    // Enable drag and drop
    const doc = this.getContentDocument();
    if (doc) {
      doc.body.addEventListener(
        'dragover',
        (e) => {
          if (!Dropzone.availableForEvent(e)) {
            return;
          }
          Dropzone.show();
        },
        false
      );
    }
  }

  // Determine type of wallpapers shown
  determineWallpaperProvider() {
    // Get current channel, route and advert_id;
    const { channel, route, session, user } = this.props;
    let { advert_id } = this.props;

    // We need a better way for this too.. the advert_id is
    // a little bit too persistent right now
    if (route.path === '/' || advert_id === undefined) {
      advert_id = false;
    }

    const tandc = session.tandc || Analytics.isEnabled();

    // A lot of directions we can take from here. These are the available destinations in order or preference:
    // 1. Local wallpapers are enabled and set. Load those.
    // 2. We're on a channel. Load channel wallpapers if available or, load Studio wallpapers.
    // 3. The terms and conditions are not accepted and we're not on a channel. Load a default onboarding wallpaper.
    // 4. An advert_id is given in the component props.
    //    Preven rotation if it is the same wallpaper.
    // 5. We have a ?wallpaper=<ID> query in our route! Directly fetch that wallpaper from s3.
    //    TODO: We need this for Sales until we update the environment, ideally
    //          it is only available in Staging maybe?
    // 6. OpenX is already running. Don't do anything
    // 7. OpenX is not running yet. Run it!
    const localWallpaperDevEnabled =
      WallpaperConfig.dev.enabled && WallpaperConfig.dev.wallpapers.length > 0;

    if (localWallpaperDevEnabled) {
      this.provider = new Predefined(WallpaperConfig.dev.wallpapers); // This allows local wallpaper development
    } else if (channel.name || user.loggedIn) {
      if (window['Wallpapers'].length > 0) {
        this.provider = new Predefined(window['Wallpapers'], advert_id);
      } else {
        this.provider = new OpenXProvider({ noAds: true });
      }
    } else if (!tandc) {
      this.provider = new Predefined(WallpaperConfig.onboarding);
    } else if (advert_id) {
      if (this.provider instanceof Predefined) {
        if (this.provider.advertId === advert_id) {
          return;
        }
        this.provider.updateAdvertId(advert_id);
      } else {
        this.provider = new Predefined(undefined, advert_id);
      }
    } else if (route.query.wallpaper) {
      const src = `https://wetransferbackgrounds-eu.s3.amazonaws.com/${
        route.query.wallpaper
      }/index.html`;
      this.provider = new Predefined([{ src }]);
    } else if (this.provider instanceof OpenXProvider) {
      return; // OpenX is already running
    } else {
      this.provider = new OpenXProvider(); // If there are no predefined wallpapers available, load OpenX
    }

    // Start loading the first media item
    this.rotateMedia();
  }

  // Get the next Media item
  async rotateMedia(media = false) {
    if (!this.provider) {
      return;
    }

    // Fetch the next media item (and create a Media object)
    // On error just try to rotate the next item.
    // (This will usually be caused by a preload error)
    if (media instanceof Media) {
      this.currentMedia = media;
    } else {
      const nextItem = await this.provider.getMedia();
      if (!nextItem && this.provider.allowEmpty) {
        return;
      }
      this.currentMedia = new Media(nextItem);
    }

    try {
      await this.renderFrameContents();
      this.sendWallpaperImpressionHit();
      // Reset the consecutive error counter
      consecutiveErrors = 0;
    } catch (e) {
      consecutiveErrors++;

      if (e) {
        TrackJS.addMetadata('file', media.file);
        TrackJS.track(e);
      }

      // Stop retrying if there is no frame, it is pointless.
      if (e === 'FrameUnavailable') {
        return;
      }

      // 3 consecutive errors? Fallback!
      // More than 3 errors? Stop
      if (consecutiveErrors === 3) {
        this.rotateMedia(Media.getFallback());
      } else if (consecutiveErrors >= 4) {
        consecutiveErrors = 0;
        return false;
      } else {
        this.rotateMedia();
      }
    }
  }

  // Register an impression using our server-side metrics
  sendWallpaperImpressionHit() {
    Metric.send(['client_wallpaper_rotations', this.provider.identifier], 5);
    this.sendSnowplowImpressionHit();
  }

  sendSnowplowImpressionHit() {
    if (!window.__snowplow__) return;

    const random = Math.random() * 100;

    // We want to send all Wallpaper impressions to Snowplow if we are in Brazil,
    // to be able to compare OpenX data with Adzerk data
    const country = this.props.session.region.country;

    // Only send 5% of wallpaper impressions
    if (country !== 'BR' && random > 5) return;

    // https://github.com/snowplow/snowplow/wiki/2-Specific-event-tracking-with-the-Javascript-tracker#36-ad-tracking-methods
    window.__snowplow__(
      'trackAdImpression:ad_impressions',
      // impressionId
      get(this.currentMedia, 'meta.id', '0').toString(),
      // costModel - 'cpa', 'cpc', or 'cpm'
      'cpm',
      // cost
      0,
      // targetUrl
      get(this.currentMedia, 'href', ''),
      // bannerId
      '',
      // zoneId
      get(this.currentMedia, 'meta.position', '0').toString(),
      // advertiserId
      '',
      // campaignId
      ''
    );
  }

  // This updates the Frame with our new Media object
  async renderFrameContents() {
    // First make sure no double timers are running.
    if (this.timer) {
      this.timer.stop();
    }

    const doc = this.getContentDocument();
    if (!doc) {
      throw 'FrameUnavailable';
    }

    const parent = doc.querySelector('.wallpapers');
    if (!parent) {
      throw 'FrameUnavailable';
    }

    // Wait until the new media is preloaded, this should return
    // an HTMLElement of sorts which we will append to the wallpapers
    // container inside the Frame.
    try {
      // Reset wallpaper styles BEFORE the wallpaper is loaded.
      // Otherwise, styles from wallpapers will be overridden by us
      this.props.resetWallpaperUIStyle();
      await Preloader.load(this.currentMedia, parent);
    } catch (e) {
      this.provider.markUnavailable(this.currentMedia);

      if (e !== 'UnsupportedMediaType') {
        throw e;
      }
    }

    if (this.currentMedia.type === 'html5') {
      this.html5Api.setWallpaper(this.currentMedia);
    }

    // Prepare the node (absolute, 0, 0, 100%, 100%)
    // Make it fullscreen (resize and reposition accordingly)
    Position.prepare(this.currentMedia);
    Position.fullscreen(this.currentMedia);

    // Start the HTML5 wallpaper
    if (this.currentMedia.type === 'html5') {
      this.html5Api.startWallpaper(this.currentMedia);
    }

    // If there are visible media items in the Frame
    // we'll want to hide those first. Then reset the
    // list of visible media.
    if (this.visibleMedia.length) {
      await Promise.all(
        this.visibleMedia.map(async (media) => {
          await media.hide();
          media.remove();
        })
      );

      this.visibleMedia = [];
    }

    // Reset HTML5 API state
    this.html5Api.reset();

    // Dress to impress, also tracking pixels.
    this.handleImpressions();

    this.html5Api.api_show();
    this.currentMedia.setClick(this.currentMedia.href);

    // Add this item to the currently visible list
    // and then fade in the media element.
    this.visibleMedia.push(this.currentMedia);
    await this.currentMedia.show();

    // The switch to the new wallpaper completed,
    // if needed you can hook into it.
    if (this.props.onComplete) {
      this.props.onComplete();
    }

    // Just in case (should not be needed) kill the rotation timer
    if (this.timer) {
      this.timer.stop();
    }

    // Rotation is not required for this provider.
    if (!this.provider.rotationEnabled) {
      return;
    }

    // If the visibility tag is enabled we want to pause timer
    // when it becomes hidden and resume when it's visible again
    if (this.currentMedia.visibility) {
      Visibility.enable((visible) => {
        if (!this.timer) {
          return;
        }

        if (visible) {
          this.timer.resume();
        } else {
          this.timer.pause();
        }
      });
    } else {
      Visibility.disable();
    }

    Wallpaper.startRotation();
  }

  repositionMedia() {
    this.visibleMedia.forEach((media) => {
      Position.fullscreen(media);
    });
  }

  // This method is used to start the advert rotation timer
  static startRotation() {
    if (!currentWallpaper) {
      return;
    }
    if (isPaused) {
      return;
    }

    const media = currentWallpaper.currentMedia;
    currentWallpaper.timer = new Timer(
      media.timer || currentWallpaper.provider.duration,
      currentWallpaper.rotateMedia.bind(currentWallpaper)
    );
  }

  // This method is used to pause the advert rotation timer
  pauseRotation() {
    if (isPaused === true) {
      return;
    }

    isPaused = true;
    if (!currentWallpaper || !currentWallpaper.timer) {
      return;
    }
    currentWallpaper.timer.pause();
  }

  // This method is used to resume the advert rotation timer
  resumeRotation() {
    if (isPaused === false) {
      return;
    }

    isPaused = false;
    if (!currentWallpaper || !currentWallpaper.timer) {
      return Wallpaper.startRotation();
    }
    currentWallpaper.timer.resume();
  }

  // We need to handle impressions
  handleImpressions() {
    // If this is not an advert we can ignore impressions
    if (!this.currentMedia.meta.id) {
      return;
    }

    let node = this.frameNode.contentDocument.querySelector('.trackers');
    // If node was not already created, create one (First wallpaper)
    if (!node) {
      node = document.createElement('div');
      node.className = 'trackers';
      node.style.display = 'none';
      this.frameNode.contentDocument.body.appendChild(node);
    }

    // Clear old impression trackers
    const removeTrackerElement = (child) => {
      node.removeChild(child);
    };
    TRACKER_ELEMENTS.static.forEach(removeTrackerElement);
    TRACKER_ELEMENTS.static = [];
    TRACKER_ELEMENTS.dynamic.forEach(removeTrackerElement);
    TRACKER_ELEMENTS.dynamic = [];
    node.innerHTML = '';

    // This method inserts static impressions
    const insertStatic = (impression) => {
      const img = document.createElement('img');
      img.src = impression;
      node.appendChild(img);

      // Push to static elements to keep track
      TRACKER_ELEMENTS.static.push(img);
    };

    const insertDynamic = (script) => {
      // Attempt to render script without executing it.
      let div = document.createElement('div');
      div.innerHTML = script;

      // Obtain the parsed script tag
      const parsed = div.querySelector('script');
      div = null; // cleanup

      // This is not a file so we don't know what to do.
      if (!parsed || !parsed.src) {
        return;
      }

      // Render a new script tag with same properties
      const loader = document.createElement('script');
      loader.async = true;
      loader.type = 'text/javascript';
      loader.src = parsed.src;
      node.appendChild(loader);

      // Push to dynamic elements to keep track
      TRACKER_ELEMENTS.dynamic.push(loader);
    };

    // Loop through all the impressions
    this.currentMedia.meta.impressions.forEach(insertStatic);

    // Loop through all the scripts
    this.currentMedia.meta.scripts.forEach(insertDynamic);

    // Prepare HTML5 Api for VAST
    const vast = new Vast(this.currentMedia.meta.vast);
    this.html5Api.setVast(vast);
  }

  // Route to a different page to close the panel
  closePanel() {
    Router.navigateTo(Router.getLastRoute('transfer', '/'));
  }

  renderWallpaperLink() {
    if (!this.currentMedia) {
      return null;
    }

    const wallpaperTitle =
      this.currentMedia.title &&
      this.currentMedia.title.length > MAX_TITLE_LENGTH
        ? this.currentMedia.title.slice(0, MAX_TITLE_LENGTH) + '...'
        : this.currentMedia.title;

    if (this.currentMedia.href && this.currentMedia.title) {
      return (
        <a
          href={this.currentMedia.href}
          rel="noopener noreferrer"
          className="wallpaper__title"
          target="_blank"
          title={this.currentMedia.title}
        >
          {wallpaperTitle}
        </a>
      );
    }

    return (
      <span className="wallpaper__title" title={this.currentMedia.title || ''}>
        {wallpaperTitle || ''}
      </span>
    );
  }

  // Render a simple iframe
  render() {
    let blockingLayer = null;
    if (this.isPanelOpen()) {
      blockingLayer = (
        <div className="wallpaper__cover" onClick={this.closePanel} />
      );
    }

    const classNames = ['wallpaper'];
    if (FeatureFlagsHelper.hasTeams) {
      classNames.push('wallpaper--with-intercom');
    }

    return (
      <div className={classNames.join(' ')} ref={(node) => (this.node = node)}>
        <iframe
          ref={(node) => (this.frameNode = node)}
          src="/blackhole/"
          scrolling="no"
          frameBorder="0"
          allow="autoplay; camera"
          onLoad={this.initFrame}
        />
        {this.renderWallpaperLink()}
        {blockingLayer}
      </div>
    );
  }
}

Wallpaper.propTypes = {
  advert_id: PropTypes.string,
  channel: PropTypes.object,
  onComplete: PropTypes.func,
  resetWallpaperUIStyle: PropTypes.func.isRequired,
  route: PropTypes.object,
  session: PropTypes.object.isRequired,
  user: PropTypes.object,
};

Wallpaper.defaultProps = {
  advert_id: '',
  channel: {},
  user: {},
};

const mapStateToProps = ({ channel, session, user }) => ({
  channel,
  session,
  user,
});
export default connect(
  mapStateToProps,
  Actions.Layout,
  null,
  { withRef: true }
)(Wallpaper);
